"use client";

import { useEffect, useState, type ReactNode } from "react";

/**
 * Renders its children only AFTER the first real user interaction
 * (scroll / pointer / key / touch / mouse move), with a timer backstop so
 * children still mount for users who never interact.
 *
 * Used to keep non-critical third-party tags (GTM, Google Ads, Meta Pixel,
 * RudderStack, Microsoft UET, Microsoft Clarity) out of the first-paint / LCP
 * window — their download + main-thread execution were inflating LCP render
 * delay on slow mobile connections.
 *
 * TRADEOFF: a visitor who lands and leaves WITHOUT any interaction before the
 * backstop fires is not tracked. This is the standard "load marketing tags on
 * interaction" pattern. To revert to immediate loading, pass `eager`.
 */
export default function DeferUntilInteraction({
  children,
  backstopMs = 4500,
  eager = false,
}: {
  children: ReactNode;
  backstopMs?: number;
  eager?: boolean;
}) {
  const [ready, setReady] = useState(eager);

  useEffect(() => {
    if (eager || ready) return;

    const events: (keyof WindowEventMap)[] = [
      "pointerdown",
      "keydown",
      "touchstart",
      "scroll",
      "mousemove",
    ];

    let fired = false;
    const trigger = () => {
      if (fired) return;
      fired = true;
      cleanup();
      setReady(true);
    };

    const timer = window.setTimeout(trigger, backstopMs);
    const cleanup = () => {
      window.clearTimeout(timer);
      events.forEach((evt) => window.removeEventListener(evt, trigger));
    };

    events.forEach((evt) =>
      window.addEventListener(evt, trigger, { once: true, passive: true }),
    );

    return cleanup;
  }, [eager, ready, backstopMs]);

  return ready ? <>{children}</> : null;
}
